import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { PenLine, Copy, CheckCircle2 } from 'lucide-react';
import { requestSnkl } from '@/services/deals.service.js';
import { api, ApiError } from '@/lib/api.js';

// Confirm dialog for "Request SNKL signature" on a Deal. POSTs to
// /api/snkl/deal/:id/request which mints a signing_tokens row and emails
// the Account primary contact a Tower link. On success the dialog shows
// the link so the broker can copy/share manually if email bounced or
// they want to text it.

export default function SnklRequestDialog({ open, onOpenChange, dealId, accountName, onChange }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [recipient, setRecipient] = useState(null); // { name, email } or { error }

  // Fetch the primary contact when the dialog opens so the broker sees
  // WHO is about to receive the email before they click send. One-shot,
  // not deduped — recipient can change deal-to-deal.
  useEffect(() => {
    if (!open || !dealId) return;
    setRecipient(null);
    api
      .get(`/api/tracon/deal/${encodeURIComponent(dealId)}`)
      .then((res) => {
        const c = res?.deal?.primary_contact || res?.deal?.contact || null;
        if (c?.email) setRecipient({ name: c.name || c.full_name || '', email: c.email });
        else setRecipient({ error: 'No primary contact email on file' });
      })
      .catch(() => setRecipient({ error: 'Could not fetch contact info' }));
  }, [open, dealId]);

  async function handleConfirm() {
    if (!dealId) return;
    setSubmitting(true);
    try {
      const res = await requestSnkl(dealId);
      if (!res?.success) {
        toast.error(`Couldn't send SNKL — ${res?.error || 'unknown error'}`);
        return;
      }
      setResult(res);
      toast.success(
        res.emailed_to
          ? `SNKL link sent to ${res.emailed_to}`
          : 'SNKL link generated — copy & share manually'
      );
      for (const w of res.warnings || []) toast.info(w);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message || 'request failed';
      toast.error(`Couldn't send SNKL: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  function copyLink() {
    if (!result?.link) return;
    navigator.clipboard.writeText(result.link)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Copy failed'));
  }

  function handleClose(open) {
    onOpenChange?.(open);
    if (!open) setResult(null);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? (
              <>
                <CheckCircle2 size={18} className="text-success" />
                Signing link generated
              </>
            ) : (
              <>
                <PenLine size={18} className="text-accent" />
                Send Statement of No Known Loss?
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {result ? (
              <>
                {result.emailed_to ? (
                  <>Email sent to <strong className="text-text">{result.emailed_to}</strong>. You can also share the link directly.</>
                ) : (
                  <>No primary contact email was on file. Copy the link below and share it manually.</>
                )}
              </>
            ) : (
              <>
                This emails a one-click Tower link to sign the <strong className="text-text">Statement of No Known Loss</strong> for{' '}
                <strong className="text-text">{accountName || 'this deal'}</strong>. The link expires in 30 days.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!result && recipient ? (
          <div className="my-2 rounded-card border border-border bg-surface p-3 text-sm">
            {recipient.error ? (
              <span className="text-warning">{recipient.error}. You'll get a link to share manually after sending.</span>
            ) : (
              <>
                <div className="text-text-muted text-xs uppercase tracking-wider mb-1">Recipient</div>
                <div className="text-text">
                  {recipient.name ? <strong>{recipient.name}</strong> : null}{' '}
                  <span className="text-text-muted">&lt;{recipient.email}&gt;</span>
                </div>
              </>
            )}
          </div>
        ) : null}

        {result?.link ? (
          <div className="my-2 rounded-card border border-border bg-bg p-3">
            <div className="break-all font-mono text-xs text-text">{result.link}</div>
          </div>
        ) : null}

        <DialogFooter>
          {result ? (
            <>
              <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
                Done
              </Button>
              <Button type="button" onClick={copyLink}>
                <Copy size={14} strokeWidth={1.5} />
                Copy link
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => handleClose(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="button" onClick={handleConfirm} disabled={submitting}>
                {submitting ? 'Sending…' : 'Send signing link'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
